import { MakeLogicType, connect, kea, path, props } from 'kea'
import { forms } from 'kea-forms'
import type { DeepPartial, DeepPartialMap, FieldName, ValidationErrorType } from 'kea-forms'

import api from 'lib/api'
import { integrationsLogic } from 'lib/integrations/integrationsLogic'
import { lemonToast } from 'lib/lemon-ui/LemonToast'
import { userLogic } from 'scenes/userLogic'

import { IntegrationType } from '~/types'

import type { UserType } from '../../../../../frontend/src/types'

export interface SlackSetupModalLogicProps {
    integration?: IntegrationType | null
    onClose: () => void
    onComplete: (integrationId?: number) => void
}

export interface SlackFormType {
    botToken: string
    channelId: string
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface slackSetupModalLogicValues {
    integrations: IntegrationType[] | null // integrationsLogic
    integrationsLoading: boolean // integrationsLogic
    slackAvailable: boolean | undefined // integrationsLogic
    user: UserType | null // userLogic
    isSlackIntegrationSubmitting: boolean
    isSlackIntegrationValid: boolean
    showSlackIntegrationErrors: boolean
    slackIntegration: {
        botToken: string
        channelId: string
    }
    slackIntegrationAllErrors: Record<string, any>
    slackIntegrationChanged: boolean
    slackIntegrationErrors: DeepPartialMap<
        {
            botToken: string
            channelId: string
        },
        ValidationErrorType
    >
    slackIntegrationHasErrors: boolean
    slackIntegrationManualErrors: Record<string, any>
    slackIntegrationTouched: boolean
    slackIntegrationTouches: Record<string, boolean>
    slackIntegrationValidationErrors: DeepPartialMap<
        {
            botToken: string
            channelId: string
        },
        ValidationErrorType
    >
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface slackSetupModalLogicActions {
    loadIntegrations: () => any // integrationsLogic
    resetSlackIntegration: (values?: { botToken: string; channelId: string }) => {
        values?: {
            botToken: string
            channelId: string
        }
    }
    setSlackIntegrationManualErrors: (errors: Record<string, any>) => {
        errors: Record<string, any>
    }
    setSlackIntegrationValue: (
        key: FieldName,
        value: any
    ) => {
        name: FieldName
        value: any
    }
    setSlackIntegrationValues: (
        values: DeepPartial<{
            botToken: string
            channelId: string
        }>
    ) => {
        values: DeepPartial<{
            botToken: string
            channelId: string
        }>
    }
    submitSlackIntegration: () => {
        value: boolean
    }
    submitSlackIntegrationFailure: (
        error: Error,
        errors: Record<string, any>
    ) => {
        error: Error
        errors: Record<string, any>
    }
    submitSlackIntegrationRequest: (slackIntegration: { botToken: string; channelId: string }) => {
        slackIntegration: {
            botToken: string
            channelId: string
        }
    }
    submitSlackIntegrationSuccess: (slackIntegration: { botToken: string; channelId: string }) => {
        slackIntegration: {
            botToken: string
            channelId: string
        }
    }
    touchSlackIntegrationField: (key: string) => {
        key: string
    }
}

export type slackSetupModalLogicType = MakeLogicType<
    slackSetupModalLogicValues,
    slackSetupModalLogicActions,
    SlackSetupModalLogicProps
>

export const slackSetupModalLogic = kea<slackSetupModalLogicType>([
    path(['products', 'workflows', 'frontend', 'SlackSetup', 'slackSetupModalLogic']),
    props({} as SlackSetupModalLogicProps),
    connect(() => ({
        values: [integrationsLogic, ['integrations', 'integrationsLoading', 'slackAvailable'], userLogic, ['user']],
        actions: [integrationsLogic, ['loadIntegrations']],
    })),
    forms(({ props, actions, values }) => ({
        slackIntegration: {
            defaults: {
                botToken: '',
                channelId: '',
            },
            errors: ({ botToken, channelId }) => ({
                botToken: botToken ? undefined : 'Bot Token is required',
                channelId: channelId ? undefined : 'Channel ID is required',
            }),
            submit: async () => {
                try {
                    const integration = await api.integrations.create({
                        kind: 'slack',
                        config: {
                            bot_token: values.slackIntegration.botToken,
                            channel_id: values.slackIntegration.channelId,
                        },
                    })
                    actions.loadIntegrations()
                    lemonToast.success('Slack integration created successfully!')
                    props.onComplete(integration.id)
                } catch (error) {
                    lemonToast.error('Failed to create Slack integration')
                    throw error
                }
            },
        },
    })),
])
